import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Check, MapPinPlus, Pencil, Trash2, Printer, Camera } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useApi, useMeta, errMsg, fmtDay } from "@/lib/api";

const IconBtn = ({ onClick, title, children, testId, accent }) => (
  <button onClick={onClick} title={title} data-testid={testId}
    className={`p-2 rounded-sm border transition-colors ${accent ? "bg-[#FF6B00] border-[#FF6B00] text-white hover:bg-[#E05D00]" : "border-slate-200 hover:border-[#0A192F]"}`}>{children}</button>
);

export function ShipmentsTable({ rows, onEdit, onEvent, onProof, refresh }) {
  const api = useApi();
  const { data: meta } = useMeta();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const term = q.trim().toLowerCase();
  const list = rows.filter((s) => (status === "all" || s.status === status) &&
    (!term || [s.tracking_number, s.sender.name, s.receiver.name, s.origin, s.destination].join(" ").toLowerCase().includes(term)));

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const approve = (s) => act(() => api("post", `/admin/shipments/${s.id}/approve`), `${s.tracking_number} approved`);
  const remove = (s) => window.confirm(`Delete ${s.tracking_number}? This cannot be undone.`) && act(() => api("delete", `/admin/shipments/${s.id}`), "Shipment deleted");

  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-slate-200">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
          <Input className="pl-9" placeholder="Search tracking number, name, city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" /></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-56" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Tracking #</TableHead><TableHead>Route</TableHead><TableHead>Receiver</TableHead><TableHead>Status</TableHead><TableHead>Booked</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} data-testid={`admin-row-${s.tracking_number}`}>
                <TableCell><Link to={`/track/${s.tracking_number}`} className="font-mono font-bold text-[#FF6B00] hover:underline">{s.tracking_number}</Link></TableCell>
                <TableCell className="min-w-[200px] text-sm">{s.origin} → {s.destination}</TableCell>
                <TableCell className="text-sm">{s.receiver.name}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDay(s.created_at)}</TableCell>
                <TableCell><div className="flex justify-end gap-2">
                  {s.status === "Pending Approval" && <IconBtn accent title="Approve booking" onClick={() => approve(s)} testId={`approve-btn-${s.tracking_number}`}><Check className="h-4 w-4" /></IconBtn>}
                  <IconBtn title="Add status update" onClick={() => onEvent(s)} testId={`event-btn-${s.tracking_number}`}><MapPinPlus className="h-4 w-4" /></IconBtn>
                  {s.status === "Delivered" && <IconBtn title="Proof of delivery photo" onClick={() => onProof(s)} testId={`proof-btn-${s.tracking_number}`}><Camera className={`h-4 w-4 ${s.proof_photo ? "text-emerald-600" : ""}`} /></IconBtn>}
                  <Link to={`/admin/label/${s.id}`} title="Print label" data-testid={`label-btn-${s.tracking_number}`} className="p-2 rounded-sm border border-slate-200 hover:border-[#0A192F] transition-colors"><Printer className="h-4 w-4" /></Link>
                  <IconBtn title="Edit" onClick={() => onEdit(s)} testId={`edit-btn-${s.tracking_number}`}><Pencil className="h-4 w-4" /></IconBtn>
                  <IconBtn title="Delete" onClick={() => remove(s)} testId={`delete-btn-${s.tracking_number}`}><Trash2 className="h-4 w-4 text-red-600" /></IconBtn>
                </div></TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500" data-testid="admin-empty">No shipments match.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
